import React from "react";
import Modal from "@app/components/modal";
import { Input, List, Typography, Alert, Checkbox } from "antd";
import "../styles/docx-import-modal.scoped.scss";

const DocxImportBaseModal = ({
    visible,
    onCancel,
    onSave,
    docName,
    setDocName,
    acceptedSections,
    setAcceptedSections,
    previewData = null,
    canSave,
    renderSection,
    isDiff,
    isLoading = false,
    includeComments = true,
    setIncludeComments,
    includeNumbers = true,
    onToggleAllNumbers,
    ...props
}) => {
    const containsInvalidImage = (node) => {
        if (!node) {
            return false;
        }
        if (Array.isArray(node)) {
            return node.some(containsInvalidImage);
        }
        const isInvalidImage = node.type === "image" && node.attrs && node.attrs.invalid;
        if (isInvalidImage) {
            return true;
        }
        if (Array.isArray(node.content)) {
            return node.content.some(containsInvalidImage);
        }
        return false;
    };

    const hasInvalidImages = Array.isArray(previewData?.sections)
        ? previewData.sections.some((sec) => containsInvalidImage(sec?.content))
        : false;

    return (
        <Modal
            visible={visible}
            title="Import DOCX Preview"
            onCancel={onCancel}
            onOk={onSave}
            okText="Save"
            cancelText="Cancel"
            okButtonProps={{ disabled: !canSave || isLoading, role: "btn-ok", loading: isLoading }}
            width={900}
            {...props}
        >
            <div className="import-modal">
                {hasInvalidImages && (
                    <Alert
                        className="alert"
                        type="warning"
                        message="Some images may not be displayed because their formats are not supported. Please check the document for any missing images."
                        style={{ marginBottom: 12 }}
                    />
                )}
                {!canSave && !isDiff && (
                    <Alert
                        className="alert"
                        type="warning"
                        message="Document name is required and at least one new or modified section must be accepted."
                        style={{ marginBottom: 12 }}
                    />
                )}
                {!canSave && isDiff && (
                    <Alert
                        className="alert"
                        type="warning"
                        message="At least one new or modified section must be accepted."
                        style={{ marginBottom: 12 }}
                    />
                )}
                <div className="field">
                    <label>
                        <b>Document name</b> <span className="required">*</span>
                    </label>
                    <Input
                        value={docName}
                        onChange={(e) => setDocName(e.target.value)}
                        placeholder="Enter document name"
                        required
                        disabled={isDiff}
                    />
                </div>
                <div className="sections">
                    <div className="sections-header">
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Sections
                        </Typography.Title>
                        {onToggleAllNumbers && (
                            <Checkbox
                                checked={includeNumbers}
                                onChange={(e) => onToggleAllNumbers(e.target.checked)}
                            >
                                Include section numbers
                            </Checkbox>
                        )}
                    </div>
                    <List
                        bordered
                        dataSource={previewData?.sections || []}
                        renderItem={renderSection}
                    />
                </div>
                <div>
                    <div className="comments-header">
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Comments
                        </Typography.Title>
                        {(previewData?.comments || []).length > 0 && setIncludeComments && (
                            <Checkbox
                                checked={includeComments}
                                onChange={(e) => setIncludeComments(e.target.checked)}
                            >
                                Include comments
                            </Checkbox>
                        )}
                    </div>
                    {(previewData?.comments || []).length === 0 && <div>No comments found.</div>}
                    {!includeComments && (previewData?.comments || []).length > 0 && (
                        <div style={{ color: "#888", fontStyle: "italic" }}>
                            Comments will not be imported.
                        </div>
                    )}
                    {includeComments && (
                        <List
                            bordered
                            dataSource={previewData?.comments || []}
                            renderItem={(comment) => (
                                <List.Item>
                                    <span>
                                        <b>Author:</b>{" "}
                                        {comment.author.external ? (
                                            <span className="external-user">
                                                EXTERNAL USER ({comment.author.fullName})
                                            </span>
                                        ) : (
                                            comment.author.fullName
                                        )}
                                        <br />
                                        <b>Text:</b> {comment.text}
                                        <br />
                                        <b>Section:</b>{" "}
                                        {(previewData?.sections || [])[comment.section]?.title ||
                                            "Unknown"}
                                    </span>
                                </List.Item>
                            )}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DocxImportBaseModal;
